import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { useRef, useCallback } from 'react';
import { streamApi, RateLimitError } from '../api.js';
import { getHeadersWithUniqueVisitorId } from '../../Auth/index.js';
import { processProductGroupTags, extractStreamingProductGroups } from '../utils/productGroupParser.js';

const useStreamingSearch = ({ brandId, appConfigId, conversationState, scrollToBottom, shouldScrollOnNextMessageRef, onStreamError, devOptions, }) => {
    const { conversationId, setConversationId, setIsActivelyStreaming, setStreamingAIResponse, setCurrentStatus, setConversationHistory, setIsWaitingForFollowUpQueries, setStreamingProductGroups, setProductMetadataMap, setSurveyMetadataMap, setPreviewSearchQueries, setPreviewQueryProducts, setFollowUpQuestions, setStreamingFeaturedProduct, setStreamingStoreLocator, setStreamingLoginRequired, setStreamingDevInfo, clearStreamingState, apiBaseUrl, isSharedView, sharedAtMessageIndex, setIsSharedView, setSourceConversationId, setSourceCheckpointIndex, } = conversationState;
    const abortControllerRef = useRef(null);
    const cancelStream = useCallback(() => {
        var _a;
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
    }, []);
    const performSearch = useCallback((searchTerm, imageData, imageMimeType, skipAddingUserMessage) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!searchTerm.trim() && !imageData) {
            setStreamingAIResponse('');
            return;
        }
        // Abort any previous in-flight stream before starting a new one
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        // Create new abort controller for this search
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        // Set actively streaming flag BEFORE any state changes
        setIsActivelyStreaming(true);
        // Add user message to conversation history immediately (unless skipped)
        shouldScrollOnNextMessageRef.current = true;
        if (!skipAddingUserMessage) {
            // Check if this is a featured product query to extract the question
            const featuredProductRegex = /<product\s+featured="true"\s+stacklineSku="([^"]+)"(?:\s+question="([^"]+)")?\s*\/>/;
            const featuredMatch = searchTerm.match(featuredProductRegex);
            const displayContent = featuredMatch && featuredMatch[2] ? featuredMatch[2] : searchTerm;
            const userMessage = Object.assign({ role: 'user', content: displayContent, timestamp: new Date() }, (imageData && {
                imageData,
                imageMimeType,
            }));
            setConversationHistory((prev) => [...prev, userMessage]);
        }
        setStreamingAIResponse('');
        setIsWaitingForFollowUpQueries(false);
        setStreamingProductGroups(new Map());
        // Clear preview data immediately when new search starts to prevent old data from showing
        setPreviewSearchQueries([]);
        setPreviewQueryProducts([]);
        let fullBuffer = '';
        let currentProducts = [];
        let currentFollowUpQuestions = [];
        let messageAddedToHistory = false; // Track if we've already added the message
        let featuredProductData = null;
        let storeLocatorData = null;
        let capturedOrders = [];
        let capturedLoginRequired = null;
        let capturedSearchQueries = [];
        let capturedQueryProducts = [];
        let capturedDevInfo;
        try {
            const headers = yield getHeadersWithUniqueVisitorId();
            yield streamApi({
                prompt: searchTerm,
                brandId: brandId || undefined,
                appConfigId: appConfigId || undefined,
                base64Image: imageData,
                imageMimeType: imageMimeType,
                // imageUrl is now optional - backend will upload if not provided
                conversationId: conversationId || undefined,
                headersToInject: headers,
                apiBaseUrl,
                devOptions,
                signal: abortController.signal,
                onStatusChange: (status) => {
                    setCurrentStatus(status);
                },
                onConversationId: (id) => {
                    // If we were in a shared view and received a new conversation ID,
                    // a fork just happened. Clear shared view and record fork origin.
                    if (isSharedView && id !== conversationId) {
                        setSourceConversationId(conversationId || undefined);
                        setSourceCheckpointIndex(sharedAtMessageIndex);
                        setIsSharedView(false);
                    }
                    setConversationId(id);
                },
                onNewProductMetadata: (products) => {
                    setProductMetadataMap((prev) => {
                        const newMap = new Map(prev);
                        products.forEach((product) => {
                            newMap.set(product.stacklineSku, product);
                        });
                        return newMap;
                    });
                    currentProducts = [...currentProducts, ...products];
                    setStreamingAIResponse(processProductGroupTags(fullBuffer));
                },
                onFeaturedProduct: (product, question) => {
                    // Store featured product data for later (when stream completes)
                    featuredProductData = { product, question };
                    // Add product to metadata map
                    setProductMetadataMap((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(product.stacklineSku, product);
                        return newMap;
                    });
                    // IMMEDIATELY set streaming featured product to show widget below user message
                    setStreamingFeaturedProduct({ product, question });
                    // Scroll to show the widget
                    scrollToBottom();
                },
                onStoreLocator: (stacklineSku) => {
                    // Store store locator data for later (when stream completes)
                    storeLocatorData = { stacklineSku };
                    // IMMEDIATELY set streaming store locator to show widget
                    setStreamingStoreLocator({ stacklineSku });
                    // Scroll to show the widget
                    scrollToBottom();
                },
                onOrders: (orders) => {
                    // Store orders for inclusion in the final message
                    capturedOrders = orders;
                },
                onSurveys: (surveys) => {
                    // Store survey metadata in the map (like products)
                    setSurveyMetadataMap((prev) => {
                        const newMap = new Map(prev);
                        surveys.forEach((survey) => {
                            newMap.set(survey.campaignId, survey);
                        });
                        return newMap;
                    });
                },
                onEvent: (event) => {
                    var _a, _b;
                    if (event.name === 'search_products_queries') {
                        // Handle both new format { searchQueries, negativeQueries } and legacy array format
                        const queries = Array.isArray(event.data) ? event.data : ((_a = event.data) === null || _a === void 0 ? void 0 : _a.searchQueries) || [];
                        capturedSearchQueries = queries;
                        setPreviewSearchQueries(queries);
                        // Capture devInfo (devOptions used + effective ranking weights) for dev mode display
                        if ((_b = event.data) === null || _b === void 0 ? void 0 : _b.devInfo) {
                            capturedDevInfo = event.data.devInfo;
                            setStreamingDevInfo(capturedDevInfo);
                            console.log('[Streaming] Captured devInfo:', JSON.stringify(capturedDevInfo, null, 2));
                        }
                    }
                    else if (event.name === 'search_no_relevant_products') {
                        // No relevant products found - the query, maxSemanticScore, and filteredOutCount are in event.data
                        // The LLM will handle messaging to the user, but we can clear any preview data
                        console.log('[Streaming] No relevant products found for query', event.data);
                        setPreviewQueryProducts([]);
                    }
                    else if (event.name === 'search_query_products') {
                        // Append to our array of query products
                        capturedQueryProducts = [...capturedQueryProducts, event.data];
                        setPreviewQueryProducts(capturedQueryProducts);
                    }
                    else if (event.name === 'follow_up_queries') {
                        currentFollowUpQuestions = event.data;
                        setIsWaitingForFollowUpQueries(false);
                        setFollowUpQuestions(event.data);
                        // If message was already added to history, update it with follow-up queries
                        if (messageAddedToHistory) {
                            setConversationHistory((prev) => {
                                const updated = [...prev];
                                const lastMessage = updated[updated.length - 1];
                                if (lastMessage && lastMessage.role === 'assistant') {
                                    updated[updated.length - 1] = Object.assign(Object.assign({}, lastMessage), { followUpQueries: event.data });
                                }
                                return updated;
                            });
                        }
                    }
                    else if (event.name === 'login_required') {
                        // Store login required data for rendering LoginWallWidget during streaming
                        conversationState.setStreamingLoginRequired(event.data);
                        // Also capture it for inclusion in the final conversation message
                        capturedLoginRequired = event.data;
                    }
                    else if (event.name === 'message_output_created') {
                        // Message content is complete - immediately finalize the message
                        // Don't wait for follow-up questions to be generated
                        setIsActivelyStreaming(false);
                        clearStreamingState();
                        // Add message to history now (follow-ups will be added when they arrive)
                        const assistantMessage = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ role: 'assistant', content: fullBuffer, timestamp: new Date(), products: currentProducts }, (featuredProductData && {
                            featuredProduct: featuredProductData,
                        })), (storeLocatorData && {
                            storeLocator: storeLocatorData,
                        })), (capturedOrders.length > 0 && {
                            orders: capturedOrders,
                        })), (capturedLoginRequired && {
                            loginRequired: capturedLoginRequired,
                        })), (capturedSearchQueries.length > 0 && {
                            previewSearchQueries: capturedSearchQueries,
                        })), (capturedQueryProducts.length > 0 && {
                            previewQueryProducts: capturedQueryProducts,
                        })), (capturedDevInfo && {
                            devInfo: capturedDevInfo,
                        }));
                        setConversationHistory((prev) => [...prev, assistantMessage]);
                        messageAddedToHistory = true;
                    }
                },
                onChunk: (chunk) => {
                    try {
                        const data = JSON.parse(chunk);
                        if (data.type === 'follow_up_questions' && data.content) {
                            currentFollowUpQuestions = data.content;
                            return;
                        }
                        else if (Array.isArray(data)) {
                            return;
                        }
                    }
                    catch (_a) {
                        // Not JSON, treat as text
                    }
                    if (chunk.includes('Stream completed.')) {
                        return;
                    }
                    fullBuffer += chunk;
                    const newStreamingGroups = extractStreamingProductGroups(fullBuffer);
                    if (newStreamingGroups.size > 0) {
                        setStreamingProductGroups(newStreamingGroups);
                    }
                    const processedText = processProductGroupTags(fullBuffer);
                    setStreamingAIResponse(processedText);
                    scrollToBottom();
                },
                onComplete: () => {
                    // Set streaming to false (may already be false from message_output_created)
                    setIsActivelyStreaming(false);
                    clearStreamingState();
                    // Only add message to history if it wasn't already added by message_output_created
                    if (!messageAddedToHistory) {
                        const assistantMessage = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ role: 'assistant', content: fullBuffer, timestamp: new Date(), products: currentProducts }, (featuredProductData && {
                            featuredProduct: featuredProductData,
                        })), (storeLocatorData && {
                            storeLocator: storeLocatorData,
                        })), (capturedOrders.length > 0 && {
                            orders: capturedOrders,
                        })), (capturedLoginRequired && {
                            loginRequired: capturedLoginRequired,
                        })), (capturedSearchQueries.length > 0 && {
                            previewSearchQueries: capturedSearchQueries,
                        })), (capturedQueryProducts.length > 0 && {
                            previewQueryProducts: capturedQueryProducts,
                        })), (capturedDevInfo && {
                            devInfo: capturedDevInfo,
                        })), (currentFollowUpQuestions.length > 0 && {
                            followUpQueries: currentFollowUpQuestions,
                        }));
                        setConversationHistory((prev) => [...prev, assistantMessage]);
                    }
                    else if (currentFollowUpQuestions.length > 0) {
                        // Message was already added, ensure follow-ups are attached
                        // (this handles the case where follow_up_queries event was missed)
                        setConversationHistory((prev) => {
                            var _a;
                            const updated = [...prev];
                            const lastMessage = updated[updated.length - 1];
                            if (lastMessage && lastMessage.role === 'assistant' && !((_a = lastMessage.followUpQueries) === null || _a === void 0 ? void 0 : _a.length)) {
                                updated[updated.length - 1] = Object.assign(Object.assign({}, lastMessage), { followUpQueries: currentFollowUpQuestions });
                            }
                            return updated;
                        });
                    }
                    setFollowUpQuestions(currentFollowUpQuestions);
                    if (currentFollowUpQuestions.length === 0) {
                        setIsWaitingForFollowUpQueries(true);
                        setTimeout(() => {
                            setIsWaitingForFollowUpQueries(false);
                        }, 3000);
                    }
                },
                onError: (error) => {
                    var _a, _b, _c, _d;
                    console.error('Stream failed:', error);
                    setIsActivelyStreaming(false);
                    setCurrentStatus('');
                    // Remove the user message that was just added since the request failed
                    if (!skipAddingUserMessage) {
                        setConversationHistory((prev) => prev.slice(0, -1));
                    }
                    // Determine error type for appropriate user messaging
                    let errorType = 'unknown';
                    if (error instanceof RateLimitError) {
                        errorType = 'rate_limit';
                    }
                    else if (error instanceof TypeError ||
                        ((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('network')) ||
                        ((_b = error.message) === null || _b === void 0 ? void 0 : _b.includes('fetch'))) {
                        errorType = 'network';
                    }
                    else if (((_c = error.message) === null || _c === void 0 ? void 0 : _c.includes('5')) && ((_d = error.message) === null || _d === void 0 ? void 0 : _d.includes('status'))) {
                        // 5xx server errors
                        errorType = 'server';
                    }
                    // Call callback so container can restore the input and show appropriate error toast
                    onStreamError === null || onStreamError === void 0 ? void 0 : onStreamError(errorType, searchTerm);
                },
            });
        }
        catch (error) {
            console.error('Search failed:', error);
            setIsActivelyStreaming(false);
        }
    }), [
        brandId,
        appConfigId,
        conversationId,
        scrollToBottom,
        setConversationHistory,
        setConversationId,
        setCurrentStatus,
        setFollowUpQuestions,
        setIsActivelyStreaming,
        setIsWaitingForFollowUpQueries,
        setPreviewQueryProducts,
        setPreviewSearchQueries,
        setProductMetadataMap,
        setSurveyMetadataMap,
        setStreamingAIResponse,
        setStreamingProductGroups,
        setStreamingFeaturedProduct,
        setStreamingStoreLocator,
        setStreamingLoginRequired,
        setStreamingDevInfo,
        clearStreamingState,
        shouldScrollOnNextMessageRef,
        apiBaseUrl,
        onStreamError,
        devOptions,
        isSharedView,
        sharedAtMessageIndex,
        setIsSharedView,
        setSourceConversationId,
        setSourceCheckpointIndex,
    ]);
    return { performSearch, cancelStream };
};

export { useStreamingSearch };
//# sourceMappingURL=useStreamingSearch.js.map
